/**
 * Hariom ERP - Reusable Form Modal
 *
 * A single pop-up form used by every module's "Add / New / Edit" button.
 * Pass a list of fields and it renders the inputs, validates required ones,
 * and hands back the values on save. Keeps all CRUD pop-ups consistent.
 */
import React, { useState } from 'react'
import toast from 'react-hot-toast'

export interface FormField {
  name: string
  label: string
  type?: 'text' | 'number' | 'select' | 'date' | 'textarea'
  options?: string[]
  placeholder?: string
  required?: boolean
  full?: boolean // span both columns
}

interface FormModalProps {
  title: string
  fields: FormField[]
  initial?: Record<string, string>
  submitLabel?: string
  onSave: (values: Record<string, string>) => void
  onClose: () => void
}

const FormModal: React.FC<FormModalProps> = ({
  title, fields, initial = {}, submitLabel = 'Save', onSave, onClose,
}) => {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {}
    fields.forEach((f) => { base[f.name] = initial[f.name] ?? '' })
    return base
  })

  const set = (name: string, v: string) =>
    setValues((prev) => ({ ...prev, [name]: v }))

  const handleSave = () => {
    const missing = fields.filter((f) => f.required && !String(values[f.name] ?? '').trim())
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((f) => f.label).join(', ')}`)
      return
    }
    onSave(values)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-steel-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-steel-400 hover:text-steel-700 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? 'col-span-2' : ''}>
              <label className="text-xs font-medium text-steel-600">
                {f.label}{f.required && ' *'}
              </label>
              {f.type === 'select' ? (
                <select
                  value={values[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  className="form-input w-full mt-1"
                >
                  <option value="">Select…</option>
                  {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea
                  value={values[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  rows={2}
                  className="form-input w-full mt-1"
                />
              ) : (
                <input
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  value={values[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  className="form-input w-full mt-1"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={handleSave} className="btn-primary text-sm">{submitLabel}</button>
        </div>
      </div>
    </div>
  )
}

export default FormModal
